import React, { useEffect, useState } from "react";

import {
    Button,
    FieldGrid,
    LabeledField,
    LanguageSegmentedControl,
    Modal,
    Stack,
    TextInput,
    Textarea,
} from "shared/ui";

import {
    MAX_TABLES_PER_BATCH,
    MAX_TABLE_NUMBER,
    MIN_TABLE_NUMBER,
} from "features/TablesManagement/constants";
import { useTablesManagementContext } from "features/TablesManagement/context";
import { toNumber } from "features/TablesManagement/lib/tableForm";
import { digitsOnly } from "shared/lib/formInput";
import { trimLocalizedMap } from "shared/lib/localizedMap";

import { LangRow, Preview, PreviewNames } from "./styles";

const DEFAULT_COUNT = "5";
const DEFAULT_TITLE = "Стол";

// Бэк вырезает цифры из шаблона и подставляет номер стола — показываем результат заранее.
const previewName = (template: string, publicId: number): string =>
    `${template.replace(/\d/g, "").trim()} ${publicId}`.trim();

export const BatchCreateDialog = () => {
    const state = useTablesManagementContext();
    const { availableLanguages, isBatchDialogOpen, locale, nextPublicId } = state;

    const [activeLang, setActiveLang] = useState<string>(locale);
    const [count, setCount] = useState<string>(DEFAULT_COUNT);
    const [startFromId, setStartFromId] = useState<string>("");
    const [title, setTitle] = useState<Record<string, string>>({});
    const [description, setDescription] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!isBatchDialogOpen) return;

        setActiveLang(locale);
        setCount(DEFAULT_COUNT);
        setStartFromId(String(nextPublicId));
        setTitle({ [locale]: DEFAULT_TITLE });
        setDescription({});
    }, [isBatchDialogOpen, locale, nextPublicId]);

    const countValue = toNumber(count);
    const startValue = toNumber(startFromId);
    const lastValue = countValue !== null && startValue !== null ? startValue + countValue - 1 : null;

    const isCountValid =
        countValue !== null && countValue >= 1 && countValue <= MAX_TABLES_PER_BATCH;
    const isRangeValid =
        startValue !== null &&
        startValue >= MIN_TABLE_NUMBER &&
        lastValue !== null &&
        lastValue <= MAX_TABLE_NUMBER;
    const isValid = isCountValid && isRangeValid;

    const skippedNumbers =
        isValid && startValue !== null && lastValue !== null
            ? state.occupiedNumbers
                  .filter((number) => number >= startValue && number <= lastValue)
                  .sort((left, right) => left - right)
            : [];

    const handleSubmit = () => {
        if (!isValid || countValue === null || startValue === null) return;

        state.submitBatch({
            count: countValue,
            startFromId: startValue,
            title: trimLocalizedMap(title),
            description: trimLocalizedMap(description),
        });
    };

    return (
        <Modal
            open={isBatchDialogOpen}
            onClose={state.closeBatchDialog}
            title="Добавить несколько столов"
            actions={
                <>
                    <Button variant="outlined" color="neutral" onClick={state.closeBatchDialog}>
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!isValid || state.saving}
                        onClick={handleSubmit}
                    >
                        Создать
                    </Button>
                </>
            }
        >
            <Stack>
                <FieldGrid $min={150}>
                    <LabeledField
                        label="Сколько столов"
                        hint={isCountValid ? undefined : `От 1 до ${MAX_TABLES_PER_BATCH}`}
                    >
                        <TextInput
                            value={count}
                            onChange={(value) => setCount(digitsOnly(value, 3))}
                            placeholder="5"
                        />
                    </LabeledField>

                    <LabeledField
                        label="Номер первого стола"
                        hint={
                            isRangeValid
                                ? undefined
                                : `Номера должны укладываться в ${MIN_TABLE_NUMBER}–${MAX_TABLE_NUMBER}`
                        }
                    >
                        <TextInput
                            value={startFromId}
                            onChange={(value) => setStartFromId(digitsOnly(value, 3))}
                            placeholder="1"
                        />
                    </LabeledField>
                </FieldGrid>

                {availableLanguages.length > 1 && (
                    <LangRow>
                        <LanguageSegmentedControl
                            languages={availableLanguages}
                            activeLang={activeLang}
                            primaryLang={locale}
                            onChange={setActiveLang}
                            isMissing={(lang) => !title[lang]}
                        />
                    </LangRow>
                )}

                <LabeledField
                    label="Название"
                    hint="К названию добавится номер стола"
                >
                    <TextInput
                        value={title[activeLang]}
                        onChange={(value) => setTitle({ ...title, [activeLang]: value })}
                        placeholder={DEFAULT_TITLE}
                    />
                </LabeledField>

                <LabeledField label="Описание">
                    <Textarea
                        value={description[activeLang]}
                        onChange={(value) =>
                            setDescription({ ...description, [activeLang]: value })
                        }
                        rows={2}
                    />
                </LabeledField>

                {isValid && (
                    <Preview>
                        Создадим столы с номерами {startValue}–{lastValue}
                        <PreviewNames>
                            {previewName(title[locale] || DEFAULT_TITLE, startValue)} …{" "}
                            {previewName(title[locale] || DEFAULT_TITLE, lastValue)}
                        </PreviewNames>
                        {skippedNumbers.length > 0 && (
                            <PreviewNames>
                                Столы {skippedNumbers.join(", ")} уже есть — они останутся
                                без изменений
                            </PreviewNames>
                        )}
                    </Preview>
                )}
            </Stack>
        </Modal>
    );
};
